import { useState, useEffect } from 'react';
import axios from 'axios';
import './admindashboard.scss';
import Header from './header';

function AdminDashboard() {
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ name: '', subcategory: '', price: '', description: '', mainImage: null, additionalImages: [] });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priceFilter, setPriceFilter] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editedPrice, setEditedPrice] = useState('');
  const [editedDescription, setEditedDescription] = useState('');

  const itemsPerPage = 20;

  const subcategories = [
    'AKCIJA!',
    'MOVI',
    'Goveđa koža',
    'Ovčija koža',
    'Svinjska koža',
    'Koža na kilogram',
    'Jednokomponentna(Žuta)',
    'Dvokomponentna(Bela)',
    'Dodatna Oprema',
    'Poluđonovi',
    'Pete',
    'Eva Program',
    'VIBRAM',
    'SVIG',
    'Ortopedski program',
    'Đonovi',
    'Farbe',
    'Perila za đonove',
    'Galanterija',
    'Alati',
    'Kozmetika',
    'Mašine',
    'Komponente'
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/products`);
      console.log('Fetched products:', response.data);

      const productsArray = Array.isArray(response.data) ? response.data : response.data.$values || [];
      setProducts(productsArray);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
  };

  const handleMainImageChange = (e) => {
    setNewProduct({ ...newProduct, mainImage: e.target.files[0] });
  };

  const handleAdditionalImagesChange = (e) => {
    setNewProduct({ ...newProduct, additionalImages: e.target.files });
  };

  const handleShiftEnter = (e) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();

      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;

      e.target.value = e.target.value.substring(0, start) + '\n' + e.target.value.substring(end);
      e.target.selectionStart = e.target.selectionEnd = start + 1;

      // Manually trigger the change event to update the state
      const event = new Event('input', { bubbles: true });
      e.target.dispatchEvent(event);
    }
  };

  const handleAddProduct = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);
  
    if (!newProduct.name || !newProduct.subcategory || !newProduct.price || !newProduct.description || !newProduct.mainImage) {
      setErrorMessage('Please fill out all fields and select an image before submitting.');
      setLoading(false);
      return;
    }
  
    const formData = new FormData();
    formData.append('name', newProduct.name);
    formData.append('subcategory', newProduct.subcategory);
    formData.append('price', newProduct.price);
    formData.append('description', newProduct.description);
    formData.append('mainImage', newProduct.mainImage);
    for (let i = 0; i < newProduct.additionalImages.length; i++) {
      formData.append('additionalImages', newProduct.additionalImages[i]);
    }
  
    try {
      await axios.post(`${import.meta.env.VITE_API_URL}/products`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchProducts();
      setSuccessMessage('Product added successfully!');
      setNewProduct({ name: '', subcategory: '', price: '', description: '', mainImage: null, additionalImages: [] });
    } catch (error) {
      console.error('Error adding product:', error);
      setErrorMessage(error.response ? error.response.data.title : error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteProduct = async (productId) => {
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/products/${productId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      setErrorMessage('Error deleting product. Please try again.');
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product.id);
    setEditedPrice(product.price);
    setEditedDescription(product.description);
  };

  const handleSaveClick = async (product) => {
    try {
      const formData = new FormData();
      formData.append('Id', product.id); 
      formData.append('Price', editedPrice);
      formData.append('Description', editedDescription);
  
      const response = await axios.put(`${import.meta.env.VITE_API_URL}/products/${product.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
  
      fetchProducts();
      setEditingProduct(null);
      console.log('Product updated successfully:', response.data);
    } catch (error) {
      console.error('Error updating product:', error);
      setErrorMessage('Error updating product. Please try again.');
    }
  };

  const handleCancelClick = () => {
    setEditingProduct(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  const handlePriceFilterChange = (e) => {
    setPriceFilter(e.target.value);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const filteredProducts = products
  .filter((product) => typeof product.name === 'string' && product.name.toLowerCase().includes(searchTerm.toLowerCase()))
  .filter((product) => (categoryFilter ? product.subcategory === categoryFilter : true))
  .filter((product) => (priceFilter ? parseFloat(product.price.replace(/[^\d.]/g, '')) <= parseFloat(priceFilter) : true));

  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const Pagination = () => (
    <div className="pagination">
      {[...Array(totalPages)].map((_, index) => (
        <button
          key={index}
          onClick={() => paginate(index + 1)}
          className={`pagination-button ${index + 1 === currentPage ? 'active' : ''}`}
        >
          {index + 1}
        </button>
      ))}
    </div>
  );

  return (
    <div>
      <Header />
      <div className="admin-dashboard">
        <div className="form-container">
          <h2>Dodaj novi produkt</h2>
          <input type="text" name="name" placeholder="Naziv" value={newProduct.name} onChange={handleInputChange} />
          <select name="subcategory" value={newProduct.subcategory} onChange={handleInputChange} required>
            <option value="" disabled>Izaberi podkategoriju</option>
            {subcategories.map((subcategory, index) => (
              <option key={index} value={subcategory}>{subcategory}</option>
            ))}
          </select>
          <input type="text" name="price" placeholder="Cena" value={newProduct.price} onChange={handleInputChange} />
          <textarea name="description" placeholder="Opis" value={newProduct.description} onChange={handleInputChange} onKeyDown={handleShiftEnter} />
          <input type="file" name="mainImage" onChange={handleMainImageChange} />
          <input type="file" name="additionalImages" multiple onChange={handleAdditionalImagesChange} />
          <button onClick={handleAddProduct} disabled={loading}>
            {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>} Dodaj produkt
          </button>
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}
        </div>
        <div className="filter-container">
          <input type="text" placeholder="Pretraga" value={searchTerm} onChange={handleSearchChange} />
          <select value={categoryFilter} onChange={handleCategoryFilterChange}>
            <option value="">Sve kategorije</option>
            {subcategories.map((category, index) => (
              <option key={index} value={category}>{category}</option>
            ))}
          </select>
          <input type="number" placeholder="Max Cena" value={priceFilter} onChange={handlePriceFilterChange} />
        </div>
        <div className="product-list">
          <h2>Lista proizvoda</h2>
          <table>
            <thead>
              <tr>
                <th>Naziv</th>
                <th>Podkategorija</th>
                <th>Cena</th>
                <th>Opis</th>
                <th>Slika</th>
                <th>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((product) => (
                <tr key={product.id}>
                  <td>{product.name}</td>
                  <td>{product.subcategory}</td>
                  <td>
                    {editingProduct === product.id ? (
                      <input
                        type="text"
                        value={editedPrice}
                        onChange={(e) => setEditedPrice(e.target.value)}
                      />
                    ) : (
                      product.price
                    )}
                  </td>
                  <td>
                    {editingProduct === product.id ? (
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        onKeyDown={handleShiftEnter}
                      />
                    ) : (
                      product.description
                    )}
                  </td>
                  <td>
                    <img
                      src={`${import.meta.env.VITE_API_URL1}${product.mainImagePath}`}
                      alt={product.name}
                      style={{ width: '50px', height: '50px' }}
                    />
                  </td>
                  <td>
                    {editingProduct === product.id ? (
                      <>
                        <button onClick={() => handleSaveClick(product)} className="save-button">Save</button>
                        <button onClick={handleCancelClick} className="cancel-button">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => handleEditClick(product)} className="edit-button">Edit</button>
                        <button onClick={() => handleDeleteProduct(product.id)} className="delete-button">Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination />
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
